import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { BreedPhoto } from './BreedPhoto';
import { useModalScrollLock } from './useModalScrollLock';
import { useAppConfig } from '../../context/AppConfigContext';
import type { PetSnapshot } from '../../types/pets';
import { ActivityLevel, AgeCategory, BodyCondition, Pathology, Protein, type DogFormData } from '../../types';

const ACTIVITY_LABELS: Record<string, string> = {
  bajo: 'Bajo',
  medio: 'Medio',
  alto: 'Alto',
};

const CONDITION_LABELS: Record<string, string> = {
  delgado: 'Delgado',
  ideal: 'Ideal',
  sobrepeso: 'Sobrepeso',
};

const PROTEIN_LABELS: Record<string, string> = {
  ninguna: 'Ninguna',
  pollo: 'Pollo',
  ternera: 'Ternera',
  cordero: 'Cordero',
  pescado: 'Pescado',
  pavo: 'Pavo',
  conejo: 'Conejo',
  cerdo: 'Cerdo',
  pato: 'Pato',
};

const PATHOLOGY_LABELS: Record<string, string> = {
  ninguna: 'Ninguna',
  alergias_alimentarias: 'Alergias alimentarias',
  sensibilidad_digestiva: 'Sensibilidad digestiva',
  sobrepeso_obesidad: 'Sobrepeso u obesidad',
  problemas_articulares: 'Problemas articulares',
  piel_pelo: 'Piel o pelo',
  dental: 'Dental',
  renal: 'Renal',
  hepatica: 'Hepática',
  diabetes: 'Diabetes',
  pancreatica: 'Pancreática',
  cancer: 'Cáncer',
  epilepsia: 'Epilepsia',
  cardiopatias: 'Cardiopatías',
  otra: 'Otra',
};

const GENERIC_BREED_KEYS = new Set(['mestizo_cruce', 'mestizo', 'no_lo_se']);
const EDITABLE_FIELDS = ['nombre', 'raza', 'edad', 'peso', 'actividad', 'condicion', 'alergias', 'patologias'] as const;
type EditableField = (typeof EDITABLE_FIELDS)[number];
const STEP_BY_FIELD: Record<EditableField, number> = {
  nombre: 2,
  raza: 3,
  edad: 4,
  peso: 4,
  actividad: 5,
  condicion: 5,
  alergias: 7,
  patologias: 8,
};
const INLINE_COMPLETED_FIELDS = new Set<EditableField>(['nombre', 'raza']);

function PetDrawerScrollLock(): null {
  useModalScrollLock();
  return null;
}

function formatBreedLabel(raza: string | null, config: ReturnType<typeof useAppConfig>['config']): string {
  if (!raza || GENERIC_BREED_KEYS.has(raza)) return 'Mestizo / Cruce';
  return config.breed_database?.breeds?.[raza]?.display_name ?? raza.replace(/_/g, ' ');
}

function normalizeSearchText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function formatAge(data: Partial<DogFormData>): string {
  const total =
    typeof data.ageTotalMonths === 'number' ? data.ageTotalMonths : (data.ageYears ?? 0) * 12 + (data.ageMonths ?? 0);
  if (total > 0 || data.ageYears !== undefined || data.ageMonths !== undefined) {
    const y = Math.floor(total / 12);
    const m = total % 12;
    if (y === 0) return `${m} mes${m !== 1 ? 'es' : ''}`;
    if (m === 0) return `${y} año${y !== 1 ? 's' : ''}`;
    return `${y}a ${m}m`;
  }
  return '—';
}

function formatList(items: string[] | undefined, noneLabel = 'Ninguna'): string {
  if (!items || items.length === 0 || items.every((i) => i === 'ninguna')) return noneLabel;
  return (
    items
      .filter((i) => i !== 'ninguna')
      .map((i) => PROTEIN_LABELS[i] ?? PATHOLOGY_LABELS[i] ?? i.replace(/_/g, ' '))
      .join(', ') || noneLabel
  );
}

function nextAgeCategory(totalMonths: number, previous?: AgeCategory): AgeCategory | undefined {
  if (totalMonths <= 0) return previous;
  if (totalMonths < 12) return AgeCategory.CACHORRO;
  if (totalMonths >= 96) return AgeCategory.SENIOR;
  return AgeCategory.ADULTO;
}

function normalizeMultiValue(current: string[], value: string, checked: boolean): string[] {
  if (value === 'ninguna') return checked ? ['ninguna'] : [];
  const withoutNone = current.filter((item) => item !== 'ninguna');
  const next = checked ? Array.from(new Set([...withoutNone, value])) : withoutNone.filter((item) => item !== value);
  return next.length > 0 ? next : ['ninguna'];
}

interface PetCardProps {
  id: string;
  name: string;
  raza: string | null;
  data: Partial<DogFormData>;
  pricing?: PetSnapshot['pricing'];
  isCurrent?: boolean;
  onUpdate: (data: Partial<DogFormData>) => void;
  onRemove?: () => void;
  onEditViaStep?: (step: number) => void;
  isCheckout?: boolean;
  onPinToTop?: () => void;
}

function PetCard({
  id,
  name,
  raza,
  data,
  pricing,
  isCurrent = false,
  onUpdate,
  onRemove,
  onEditViaStep,
  isCheckout = false,
  onPinToTop,
}: PetCardProps) {
  const { config } = useAppConfig();
  const [editingField, setEditingField] = useState<EditableField | null>(null);
  const [draft, setDraft] = useState<Partial<DogFormData>>(data);
  const [breedQuery, setBreedQuery] = useState('');
  const [showMobileDetails, setShowMobileDetails] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const razaDisplay = formatBreedLabel(raza, config);
  const breedOptions = useMemo(() => {
    const breeds = config.breed_database?.breeds ?? {};
    return [
      { value: 'mestizo_cruce', label: 'Mestizo / Cruce' },
      ...Object.entries(breeds).map(([value, breed]) => ({
        value,
        label: breed.display_name ?? value.replace(/_/g, ' '),
      })),
    ].sort((a, b) => a.label.localeCompare(b.label, 'es'));
  }, [config.breed_database?.breeds]);
  const filteredBreedOptions = useMemo(() => {
    const query = normalizeSearchText(breedQuery);
    if (!query) return breedOptions.slice(0, 6);
    return breedOptions.filter((breed) => normalizeSearchText(breed.label).includes(query)).slice(0, 6);
  }, [breedOptions, breedQuery]);

  useEffect(() => {
    setDraft(data);
  }, [data]);

  function startEditing(field: EditableField) {
    setDraft(data);
    if (field === 'raza') {
      setBreedQuery(formatBreedLabel(data.raza ?? null, config));
    }
    setEditingField(field);
  }

  function saveEditing() {
    if (!editingField) return;
    if (editingField === 'edad') {
      const years = Math.max(0, Number(draft.ageYears ?? 0));
      const months = Math.max(0, Math.min(11, Number(draft.ageMonths ?? 0)));
      const totalMonths = years * 12 + months;
      onUpdate({
        ageYears: years,
        ageMonths: months,
        ageTotalMonths: totalMonths,
        ageCategory: nextAgeCategory(totalMonths, data.ageCategory),
      });
    } else {
      onUpdate(draft);
    }
    setEditingField(null);
  }

  function cancelEditing() {
    setDraft(data);
    setBreedQuery('');
    setEditingField(null);
  }

  function renderEditButton(field: EditableField): React.ReactNode {
    const mustRecalculate = !isCurrent && !INLINE_COMPLETED_FIELDS.has(field);
    return (
      <button
        type="button"
        onClick={() => {
          if (mustRecalculate) {
            onEditViaStep?.(STEP_BY_FIELD[field]);
            return;
          }
          startEditing(field);
        }}
        className="ml-2 px-2 py-0.5 rounded-md bg-[#008BBC]/10 text-[#008BBC] hover:bg-[#008BBC]/20 text-[11px] font-medium shrink-0 transition-colors"
        aria-label={isCurrent ? `Editar ${field}` : `Editar ${field} de ${name}`}
        data-testid={`edit-${field}`}
      >
        Editar
      </button>
    );
  }

  function renderEditor(field: EditableField): React.ReactNode {
    if (editingField !== field) return null;
    const fieldId = `${id}-${field}`;
    const inputClass =
      'w-full rounded-lg border border-gray-200 px-2.5 py-2 text-sm focus:border-[#008BBC] focus:outline-none';
    let control: React.ReactNode;

    if (field === 'nombre') {
      control = (
        <input
          id={fieldId}
          className={inputClass}
          value={draft.nombre ?? ''}
          onChange={(e) => setDraft((prev) => ({ ...prev, nombre: e.target.value }))}
        />
      );
    } else if (field === 'raza') {
      control = (
        <div className="space-y-2">
          <input
            id={fieldId}
            className={inputClass}
            value={breedQuery}
            placeholder="Buscar raza..."
            aria-label="Buscar raza"
            onChange={(e) => setBreedQuery(e.target.value)}
          />
          <div className="grid gap-1 max-h-44 overflow-y-auto pr-1">
            {filteredBreedOptions.map((breed) => {
              const isSelected = (draft.raza ?? 'mestizo_cruce') === breed.value;
              return (
                <button
                  key={breed.value}
                  type="button"
                  onClick={() => {
                    setDraft((prev) => ({ ...prev, raza: breed.value }));
                    setBreedQuery(breed.label);
                  }}
                  className={`w-full rounded-lg border px-2.5 py-1.5 text-left text-xs font-medium transition-colors ${
                    isSelected
                      ? 'border-[#008BBC] bg-[#008BBC]/10 text-[#008BBC]'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-[#008BBC]/40'
                  }`}
                  aria-pressed={isSelected}
                >
                  {breed.label}
                </button>
              );
            })}
          </div>
          {filteredBreedOptions.length === 0 && (
            <p className="text-[11px] text-gray-500">No encontramos esa raza. Puedes elegir Mestizo / Cruce.</p>
          )}
        </div>
      );
    } else if (field === 'edad') {
      control = (
        <div className="grid grid-cols-2 gap-2">
          <label className="text-[11px] text-gray-500">
            Años
            <input
              className={`${inputClass} mt-1`}
              type="number"
              min={0}
              value={draft.ageYears ?? 0}
              onChange={(e) => setDraft((prev) => ({ ...prev, ageYears: Number(e.target.value) }))}
            />
          </label>
          <label className="text-[11px] text-gray-500">
            Meses
            <input
              className={`${inputClass} mt-1`}
              type="number"
              min={0}
              max={11}
              value={draft.ageMonths ?? 0}
              onChange={(e) => setDraft((prev) => ({ ...prev, ageMonths: Number(e.target.value) }))}
            />
          </label>
        </div>
      );
    } else if (field === 'peso') {
      control = (
        <input
          id={fieldId}
          className={inputClass}
          type="number"
          min={0}
          step="0.1"
          value={draft.peso ?? ''}
          onChange={(e) => setDraft((prev) => ({ ...prev, peso: Number(e.target.value) }))}
        />
      );
    } else if (field === 'actividad') {
      control = (
        <select
          id={fieldId}
          className={inputClass}
          value={draft.activityLevel ?? ActivityLevel.MEDIO}
          onChange={(e) =>
            setDraft((prev) => ({ ...prev, activityLevel: e.target.value as ActivityLevel, activityLevelChosen: true }))
          }
        >
          {Object.entries(ACTIVITY_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      );
    } else if (field === 'condicion') {
      control = (
        <select
          id={fieldId}
          className={inputClass}
          value={draft.bodyCondition ?? BodyCondition.IDEAL}
          onChange={(e) => setDraft((prev) => ({ ...prev, bodyCondition: e.target.value as BodyCondition }))}
        >
          {Object.entries(CONDITION_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      );
    } else {
      const isAllergies = field === 'alergias';
      const current = ((isAllergies ? draft.proteinAllergies : draft.pathologies) ?? ['ninguna']) as string[];
      const options = isAllergies ? Object.values(Protein) : Object.values(Pathology);
      const labels = isAllergies ? PROTEIN_LABELS : PATHOLOGY_LABELS;
      control = (
        <div className={`grid gap-1.5 ${isAllergies ? 'grid-cols-2' : 'grid-cols-1'}`}>
          {options.map((value) => (
            <label key={value} className="flex items-center gap-2 text-xs text-gray-700">
              <input
                type="checkbox"
                checked={current.includes(value)}
                onChange={(e) => {
                  const next = normalizeMultiValue(current, value, e.target.checked);
                  setDraft((prev) =>
                    isAllergies ? { ...prev, proteinAllergies: next } : { ...prev, pathologies: next as Pathology[] },
                  );
                }}
              />
              {labels[value] ?? value}
            </label>
          ))}
        </div>
      );
    }

    return (
      <div className="mt-2 rounded-xl bg-[#F7F4EE] p-2.5">
        {control}
        <div className="mt-2 flex justify-end gap-2">
          <button
            type="button"
            onClick={cancelEditing}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-500 hover:bg-white"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={saveEditing}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-[#008BBC] hover:bg-[#007aa8]"
          >
            Guardar
          </button>
        </div>
      </div>
    );
  }

  function renderRow(label: string, value: string, field: EditableField, mobilePriority = false): React.ReactNode {
    const isHiddenOnMobile = !mobilePriority && !showMobileDetails && editingField !== field;
    return (
      <div className={`py-1.5 border-b border-gray-100 last:border-0 ${isHiddenOnMobile ? 'hidden sm:block' : ''}`}>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 w-20 shrink-0">{label}</span>
          <div className="flex items-center flex-1 justify-end min-w-0">
            <span className="text-xs text-gray-800 font-medium text-right truncate">{value}</span>
            {renderEditButton(field)}
          </div>
        </div>
        {renderEditor(field)}
      </div>
    );
  }

  return (
    <div
      className={`rounded-xl border p-3 mb-3 ${
        isCurrent ? 'border-[#008BBC]/30 bg-[#008BBC]/5' : 'border-gray-200 bg-white'
      }`}
      data-pet-id={id}
    >
      <div
        className={`flex items-center gap-2 mb-2.5 ${isCheckout ? 'cursor-pointer rounded-lg -mx-1 px-1 hover:bg-black/5 transition-colors' : ''}`}
        onClick={isCheckout ? onPinToTop : undefined}
        role={isCheckout ? 'button' : undefined}
        aria-label={isCheckout ? `Editar a ${name}` : undefined}
      >
        <BreedPhoto breedKey={raza} className="w-10 h-10 shrink-0" alt={name} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1 flex-wrap">
            <span className="font-semibold text-sm text-gray-800">{name}</span>
            {isCurrent && !isCheckout && (
              <span className="text-[10px] font-bold uppercase tracking-wide bg-[#008BBC] text-white rounded-full px-1.5 py-0.5 ml-1">
                En curso
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500 truncate">{razaDisplay}</div>
        </div>
        {onRemove && (
          <button
            type="button"
            onClick={() => setConfirmingRemove(true)}
            className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors shrink-0"
            aria-label={`Eliminar ${name}`}
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>

      <div>
        {renderRow('Nombre', name, 'nombre', true)}
        {renderRow('Raza', razaDisplay, 'raza', true)}
        {renderRow('Edad', formatAge(data), 'edad', true)}
        {renderRow('Peso', data.peso != null ? `${data.peso} kg` : '—', 'peso', true)}
        {renderRow('Actividad', ACTIVITY_LABELS[data.activityLevel ?? ''] ?? '—', 'actividad')}
        {renderRow('Condición', CONDITION_LABELS[data.bodyCondition ?? ''] ?? '—', 'condicion')}
        {renderRow('Alergias', formatList(data.proteinAllergies), 'alergias')}
        {renderRow('Patologías', formatList(data.pathologies), 'patologias')}
      </div>

      <button
        type="button"
        onClick={() => setShowMobileDetails((value) => !value)}
        className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg bg-[#F7F4EE] px-3 py-2 text-xs font-semibold text-[#008BBC] sm:hidden"
        aria-expanded={showMobileDetails}
      >
        {showMobileDetails ? 'Ver menos' : 'Ver más'}
        {showMobileDetails ? (
          <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
        )}
      </button>

      {confirmingRemove && onRemove && (
        <div className="mt-3 rounded-xl border border-red-100 bg-red-50 p-3">
          <p className="text-xs font-medium text-red-900">¿Borrar a {name} del plan?</p>
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setConfirmingRemove(false)}
              className="rounded-lg px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-white"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => {
                setConfirmingRemove(false);
                onRemove();
              }}
              className="rounded-lg bg-red-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-600"
            >
              Borrar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export interface PetDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  petsCompleted: PetSnapshot[];
  formData: Partial<DogFormData>;
  onEditField?: (step: number) => void;
  onEditCompletedField?: (petId: string, step: number) => void;
  onUpdateCurrentPet?: (data: Partial<DogFormData>) => void;
  onUpdateCompletedPet?: (petId: string, data: Partial<DogFormData>) => void;
  onRemoveCurrentPet?: () => void;
  onRemoveCompletedPet?: (petId: string) => void;
  firstPetId?: string;
  isCheckout?: boolean;
}

const PetDrawer: React.FC<PetDrawerProps> = ({
  isOpen,
  onClose,
  petsCompleted,
  formData,
  onEditCompletedField,
  onUpdateCurrentPet = () => undefined,
  onUpdateCompletedPet = () => undefined,
  onRemoveCurrentPet,
  onRemoveCompletedPet,
  firstPetId,
  isCheckout = false,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [activePetId, setActivePetId] = useState<string | undefined>(undefined);
  const [expandedExtra, setExpandedExtra] = useState(false);
  const currentName = (formData.nombre ?? '').trim();

  const sortedPets =
    firstPetId && firstPetId !== 'current'
      ? [...petsCompleted].sort((a, b) => {
          if (a.id === firstPetId) return -1;
          if (b.id === firstPetId) return 1;
          return 0;
        })
      : petsCompleted;
  const showCurrentFirst = firstPetId === 'current';
  const totalVisiblePets = petsCompleted.length + (currentName.length > 0 ? 1 : 0);
  const canRemovePets = totalVisiblePets > 1;

  // In checkout mode, unified sorted list (current pet is also "completed")
  type CheckoutPetItem = {
    id: string;
    nombre: string;
    raza: string | null;
    data: Partial<DogFormData>;
    pricing?: PetSnapshot['pricing'];
    isCurrent: boolean;
  };

  const checkoutPets: CheckoutPetItem[] = useMemo(() => {
    if (!isCheckout) return [];
    const result: CheckoutPetItem[] = [];
    if (currentName.length > 0) {
      result.push({ id: 'current', nombre: currentName, raza: formData.raza ?? null, data: formData, isCurrent: true });
    }
    sortedPets.forEach((pet) => {
      result.push({
        id: pet.id,
        nombre: pet.nombre,
        raza: pet.raza,
        data: pet.formData,
        pricing: pet.pricing,
        isCurrent: false,
      });
    });
    if (activePetId) {
      const idx = result.findIndex((p) => p.id === activePetId);
      if (idx > 0) {
        const [pinned] = result.splice(idx, 1);
        result.unshift(pinned);
      }
    }
    return result;
  }, [isCheckout, currentName, formData, sortedPets, activePetId]);

  useEffect(() => {
    if (!isOpen) {
      setActivePetId(undefined);
      setExpandedExtra(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollTop = 0;
  }, [isOpen, firstPetId, activePetId]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isOpen && event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const renderCheckoutPetCard = (pet: CheckoutPetItem) => (
    <PetCard
      key={pet.id}
      id={pet.id}
      name={pet.nombre}
      raza={pet.raza}
      data={pet.data}
      pricing={pet.pricing}
      isCurrent={pet.isCurrent}
      isCheckout={true}
      onPinToTop={() => setActivePetId(pet.id)}
      onUpdate={pet.isCurrent ? onUpdateCurrentPet : (data) => onUpdateCompletedPet(pet.id, data)}
      onRemove={canRemovePets ? (pet.isCurrent ? onRemoveCurrentPet : () => onRemoveCompletedPet?.(pet.id)) : undefined}
      onEditViaStep={pet.isCurrent ? undefined : (step) => onEditCompletedField?.(pet.id, step)}
    />
  );

  const currentCard =
    currentName.length > 0 ? (
      <PetCard
        id="current"
        name={currentName}
        raza={formData.raza ?? null}
        data={formData}
        isCurrent={true}
        onUpdate={onUpdateCurrentPet}
        onRemove={canRemovePets ? onRemoveCurrentPet : undefined}
      />
    ) : null;

  return (
    <>
      {isOpen && <PetDrawerScrollLock />}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/20"
          onClick={onClose}
          aria-hidden="true"
          data-testid="drawer-backdrop"
        />
      )}
      <div
        role="dialog"
        aria-label="Mis mascotas"
        aria-modal="true"
        aria-hidden={!isOpen}
        className="fixed right-0 top-0 bottom-0 z-50 bg-white shadow-xl transition-transform duration-300 ease-out flex flex-col w-[88vw] sm:w-[390px] lg:w-[380px] rounded-l-2xl"
        style={{ transform: isOpen ? 'translateX(0)' : 'translateX(100%)' }}
        data-testid="pet-drawer"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 shrink-0">
          <h2 className="font-semibold text-gray-800 text-sm">Mis mascotas</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-7 h-7 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors text-base"
            aria-label="Cerrar"
          >
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-3 py-3" ref={scrollContainerRef}>
          {isCheckout ? (
            <>
              {checkoutPets.slice(0, 2).map(renderCheckoutPetCard)}
              {checkoutPets.length > 2 && (
                <>
                  {expandedExtra && checkoutPets.slice(2).map(renderCheckoutPetCard)}
                  <button
                    type="button"
                    onClick={() => setExpandedExtra((v) => !v)}
                    className="w-full flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 font-poppins text-xs font-semibold text-[#008BBC] hover:bg-gray-100 transition-colors mb-3"
                  >
                    {expandedExtra ? (
                      <>
                        Ver menos <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
                      </>
                    ) : (
                      <>
                        {checkoutPets.length - 2} mascota{checkoutPets.length - 2 !== 1 ? 's' : ''} más{' '}
                        <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                      </>
                    )}
                  </button>
                </>
              )}
            </>
          ) : (
            <>
              {showCurrentFirst && currentCard}
              {sortedPets.map((pet) => (
                <PetCard
                  key={pet.id}
                  id={pet.id}
                  name={pet.nombre}
                  raza={pet.raza}
                  data={pet.formData}
                  pricing={pet.pricing}
                  isCurrent={false}
                  onUpdate={(data) => onUpdateCompletedPet(pet.id, data)}
                  onRemove={canRemovePets ? () => onRemoveCompletedPet?.(pet.id) : undefined}
                  onEditViaStep={(step) => onEditCompletedField?.(pet.id, step)}
                />
              ))}
              {!showCurrentFirst && currentCard}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default PetDrawer;
